// @flow

import React, { type Node, useMemo } from 'react';
import styled from 'styled-components';

import { type UserInputName } from '@tectonics/prs/user-input';
import { type Country, getCountryNationality } from '@tectonics/prs/country';
import { camelToSnakeCase, snakeToPascalCase } from '@tectonics/utils/string';
import { type Feature, type FeatureName, hasFeature, IfFeature } from '@tectonics/prs/feature';
import { useCurrentAllFeatures } from '@tectonics/prs/payment-spec';
import { SelectedCardBrandProvider } from '@tectonics/look-and-feel/commons/selected-card-brand';
import { useUserInputValue } from '@tectonics/prs/user-input-values';
import { detectCardBrand } from '@tectonics/prs/card-brand';
import { useScreenWidth } from '@tectonics/hooks/use-screen-width';
import { getCountry, useIncomingPayment } from '@tectonics/prs/incoming-payment';
import { FieldCardSecurityCode, FieldCardSecurityCodeAlert } from '@tectonics/components/form/fields/card-security-code';
import { FieldCardExpiryDate } from './fields/field-card-expiry-date';
import { FieldCardNumber } from './fields/field-card-number';
import { FieldCardHolderName } from './fields/field-card-holder-name';
import { FieldCardInstalments } from './fields/field-card-instalments';
import { FieldAcceptVisaTerms } from './fields/field-accept-visa-terms';
import { SaveOneClickCard } from './fields/save-one-click-card';
import { FieldSouthAfricanDocument, FieldSouthAfricanState } from '../../customer-form/south-african';
import { DisplayBrazilianDebitcardBanksInfo } from './display-bank-info/display-brazilian-debitcard-banks-info';
import { DisplayParaguayanCreditcardBanksInfo } from './display-bank-info/display-paraguayan-creditcard-banks-info';
import {
  FieldMexicanAddress, FieldMexicanCity,
  FieldMexicanState, FieldMexicanStreetComplement,
  FieldMexicanStreetNumber, FieldMexicanZipcode,
} from '../../customer-form/mexican';

import {
  FieldArgentineAddress, FieldArgentineCity,
  FieldArgentineState, FieldArgentineStreetComplement,
  FieldArgentineStreetNumber, FieldArgentineZipcode,
} from '../../customer-form/argentine';

import {
  FieldAddress, FieldCity,
  FieldState, FieldStreetComplement,
  FieldStreetNumber, FieldZipcode,
} from '../../customer-form/commons';

const Root = styled.div`
  display: grid;
  grid-gap: 2rem;
  grid-template-columns: 1fr 1fr;
  grid-auto-flow: dense;

  & > * {
    grid-column: 1/3;
  }
`;

export function CardForm(): Node {
  const [cardNumber] = useUserInputValue('cardNumber');
  const screenWidth = useScreenWidth();

  const cardBrand = useMemo(() => {
    return detectCardBrand(cardNumber);
  }, [cardNumber]);

  const isMobile = screenWidth <= 640;

  return (
    <SelectedCardBrandProvider cardBrand={cardBrand}>
      <Root>
        <DisplaySelectedCardBrandProvider isMobile={isMobile} />
        <SaveOneClickCard />
      </Root>
    </SelectedCardBrandProvider>
  );
}

type Props = {
  isMobile: boolean,
};

const Section = styled.p`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.colors.secondary(30)};
  font-family: ${props => props.theme.fontFamily.display};

  &:first-child {
    margin-top: 16px;
  }

  &:last-child {
    display: none;
  }
`;

function DisplaySelectedCardBrandProvider({ isMobile }: Props): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'mx':
    case 'ar':
    case 'co':
    case 'pe':
    case 'cl':
    case 'uy':
      return (
        <>
          <Section>Datos de la tarjeta</Section>
          <DisplayBankInfoComponent/>
          <CardFields isMobile={isMobile}/>
          <Section>Tu Dirección</Section>
          <DisplayAddressInfoComponent isMobile={isMobile}/>
        </>
      );
    case 'za':
      return (
        <>
          <DisplayBankInfoComponent/>
          <FieldSouthAfricanDocument />
          <DisplayAddressInfoComponent isMobile={isMobile}/>
          <CardFields isMobile={isMobile}/>
        </>
      );
    default:
      return (
        <>
          <DisplayBankInfoComponent/>
          <CardFields isMobile={isMobile}/>
          <DisplayAddressInfoComponent isMobile={isMobile}/>
        </>
      );
  }
}

function DisplayAddressInfoComponent({ isMobile }: Props): Node {
  return (
    <>
      <FieldZipcodeByCountry/>
      <FieldStateByCountry rootStyle={{gridColumn: isMobile ? '1/3' : '1/2'}}/>
      <FieldCityByCountry rootStyle={{gridColumn: isMobile ? '1/3' : '2/3'}}/>
      <FieldStreetAddressByCountry/>
      <FieldStreetNumberByCountry rootStyle={{gridColumn: isMobile ? '1/3' : '1/2'}}/>
      <FieldStreetComplementByCountry rootStyle={{gridColumn: isMobile ? '1/3' : '2/3'}}/>
    </>
  );
}

function DisplayBankInfoComponent(): Node {
  const allFeatures = useCurrentAllFeatures();

  return (
    <>
      <IfFeature allFeatures={allFeatures} wanted="display_paraguayan_creditcard_banks_info">
        <DisplayParaguayanCreditcardBanksInfo />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="display_brazilian_debitcard_banks_info">
        <DisplayBrazilianDebitcardBanksInfo />
      </IfFeature>
    </>
  );
}

function CardFields(): Node {
  const allFeatures = useCurrentAllFeatures();

  return (
    <>
      <IfFeature allFeatures={allFeatures} wanted="field_card_holder_name">
        <FieldCardHolderName />
      </IfFeature>

      <div className="pt-4">
        <IfFeature allFeatures={allFeatures} wanted="field_card_number">
          <FieldCardNumber />
        </IfFeature>
      </div>

      <div className="flex flex-col gap-8 md:flex-row md:gap-6">
        <div className="flex-1">
          <IfFeature allFeatures={allFeatures} wanted="field_card_expiry_date">
            <FieldCardExpiryDate />
          </IfFeature>
        </div>

        <div className="flex-1">
          <IfFeature allFeatures={allFeatures} wanted="field_card_security_code">
            <FieldCardSecurityCode />
          </IfFeature>
        </div>
      </div>

      <IfFeature allFeatures={allFeatures} wanted="field_card_security_code">
        <FieldCardSecurityCodeAlert />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_card_instalments">
        <FieldCardInstalments />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="field_accept_visa_terms">
        <FieldAcceptVisaTerms />
      </IfFeature>
    </>
  );
}

const countriesWithAddressFields: ImmutableArray<Country> = Object.freeze([
  'ar', 'mx', 'co', 'pe', 'cl', 'uy', 'pa', 'gt', 'sv', 'do', 'cr', 'py', 'za',
]);

function shouldHideAddressField(
  country: Country,
  allFeatures: ImmutableArray<Feature>,
  featureName: FeatureName
): boolean {
  return !countriesWithAddressFields.includes(country) || !hasFeature(allFeatures, featureName);
}

function getFieldAndFeatureName(country: Country, snakeCaseFieldSuffix: string): [UserInputName, FeatureName] {
  const countryNationality = getCountryNationality(country);
  const fieldName = `${countryNationality}${snakeToPascalCase(snakeCaseFieldSuffix)}`;
  const featureName = `field_${camelToSnakeCase(countryNationality)}_${snakeCaseFieldSuffix}`;

  return [fieldName, featureName];
}

type RootStyleProps = {|
  rootStyle: {},
|}

function FieldZipcodeByCountry(): Node {
  const incomingPayment = useIncomingPayment();
  const allFeatures = useCurrentAllFeatures();

  const country = getCountry(incomingPayment);
  const [fieldName, featureName] = getFieldAndFeatureName(country, 'zipcode');

  if (shouldHideAddressField(country, allFeatures, featureName)) {
    return null;
  }

  switch (country) {
    case 'ar':
      return <FieldArgentineZipcode />;
    case 'mx':
      return <FieldMexicanZipcode />;
    default:
      return <FieldZipcode fieldName={fieldName} />;
  }
}

function FieldStateByCountry({ rootStyle }: RootStyleProps): Node {
  const incomingPayment = useIncomingPayment();
  const allFeatures = useCurrentAllFeatures();

  const country = getCountry(incomingPayment);
  const [fieldName, featureName] = getFieldAndFeatureName(country, 'state');

  if (shouldHideAddressField(country, allFeatures, featureName)) {
    return null;
  }

  switch (country) {
    case 'ar':
      return <FieldArgentineState rootStyle={rootStyle} />;
    case 'mx':
      return <FieldMexicanState rootStyle={rootStyle} />;
    case 'za':
      return <FieldSouthAfricanState rootStyle={rootStyle} />;
    default:
      return <FieldState fieldName={fieldName} rootStyle={rootStyle} />;
  }
}

function FieldCityByCountry({ rootStyle }: RootStyleProps): Node {
  const incomingPayment = useIncomingPayment();
  const allFeatures = useCurrentAllFeatures();

  const country = getCountry(incomingPayment);
  const [fieldName, featureName] = getFieldAndFeatureName(country, 'city');

  if (shouldHideAddressField(country, allFeatures, featureName)) {
    return null;
  }

  switch (country) {
    case 'ar':
      return <FieldArgentineCity rootStyle={rootStyle} />;
    case 'mx':
      return <FieldMexicanCity rootStyle={rootStyle} />;
    default:
      return <FieldCity fieldName={fieldName} rootStyle={rootStyle} />;
  }
}

function FieldStreetAddressByCountry(): Node {
  const incomingPayment = useIncomingPayment();
  const allFeatures = useCurrentAllFeatures();

  const country = getCountry(incomingPayment);
  const [fieldName, featureName] = getFieldAndFeatureName(country, 'address');

  if (shouldHideAddressField(country, allFeatures, featureName)) {
    return null;
  }

  switch (country) {
    case 'ar':
      return <FieldArgentineAddress />;
    case 'mx':
      return <FieldMexicanAddress />;
    default:
      return <FieldAddress fieldName={fieldName} />;
  }
}

function FieldStreetNumberByCountry({ rootStyle }: RootStyleProps): Node {
  const incomingPayment = useIncomingPayment();
  const allFeatures = useCurrentAllFeatures();

  const country = getCountry(incomingPayment);
  const [fieldName, featureName] = getFieldAndFeatureName(country, 'street_number');

  if (shouldHideAddressField(country, allFeatures, featureName)) {
    return null;
  }

  switch (country) {
    case 'ar':
      return <FieldArgentineStreetNumber rootStyle={rootStyle} />;
    case 'mx':
      return <FieldMexicanStreetNumber rootStyle={rootStyle} />;
    default:
      return <FieldStreetNumber fieldName={fieldName} rootStyle={rootStyle} />;
  }
}

function FieldStreetComplementByCountry({ rootStyle }: RootStyleProps): Node {
  const incomingPayment = useIncomingPayment();
  const allFeatures = useCurrentAllFeatures();

  const country = getCountry(incomingPayment);
  const [fieldName, featureName] = getFieldAndFeatureName(country, 'street_complement');

  if (shouldHideAddressField(country, allFeatures, featureName)) {
    return null;
  }

  switch (country) {
    case 'ar':
      return <FieldArgentineStreetComplement rootStyle={rootStyle} />;
    case 'mx':
      return <FieldMexicanStreetComplement rootStyle={rootStyle} />;
    default:
      return <FieldStreetComplement fieldName={fieldName} rootStyle={rootStyle} />;
  }
}
